import { useRoleLabels } from "../lib/RoleLabelsContext";
import { exerciseNow, setExerciseTimeout, clearExerciseTimeout } from "../exercises/pause-clock";
import { useLearningDisplayItem } from "../exercises/LearningDisplayBoundary";
import { RecommendationPlanFields, emptyRecommendationPlan, recommendationPlanInput } from "./RecommendationPlanFields";
import { useRecommendationRunStarter } from "../exercises/recommendation-context";
import { useEffect, useMemo, useRef, useState } from "react";
import type { MatheExerciseConfig, MatheOperation, MatheRunSettings } from "@calega/content";
import type { MatheRun } from "@calega/exercise-engine";
import type { Account } from "../lib/types";
import { api } from "../lib/api";
import { useRealtime } from "../realtime/RealtimeContext";
import { ExerciseMenu } from "./ExerciseMenu";
import { ExerciseWaitingCard } from "./ExerciseWaitingCard";
import { ExerciseDurationSelect } from "./ExerciseSettings";
import { abortExerciseRun, startAdaptiveAssignmentRun, submitExerciseAnswer, submitSkippedExerciseAnswer } from "../exercises/learning-api";
import { CompletedRunActions } from "./CompletedRunActions";
import { CompletedRunHistory } from "./CompletedRunHistory";
import { RunCompletion, useRunCompletion } from "./RunCompletion";

type Phase = "running" | "feedback" | "complete";
interface MatheAnswer { itemId: string; value: number; expected: number; correct: boolean }
interface State {
  runId: string;
  run: MatheRun;
  index: number;
  phase: Phase;
  value: string;
  correctionCount: number;
  intermediateValues: number[];
  answers: MatheAnswer[];
  feedback: MatheAnswer | null;
  endsAt: number | null;
  result: { score: number; maxScore: number } | null;
}
interface AdaptiveLearner { id: string; displayName: string }

const ranges: Array<{ id: MatheRunSettings["range"]; label: string }> = [
  { id: "0-10", label: "0–10" }, { id: "0-20", label: "0–20" }, { id: "20-50", label: "20–50" },
  { id: "20-100", label: "20–100" }, { id: "100-200", label: "100–200" },
  { id: "100-1000", label: "100–1.000" }, { id: "1000-100000", label: "1.000–100.000" },
];
const operations: Array<{ id: MatheOperation; label: string; symbol: string }> = [
  { id: "addition", label: "Addition", symbol: "+" },
  { id: "subtraction", label: "Subtraktion", symbol: "−" },
  { id: "multiplication", label: "Multiplikation", symbol: "×" },
  { id: "division", label: "Division", symbol: "÷" },
];

function operationSymbol(operation: MatheOperation) {
  return operations.find(({ id }) => id === operation)?.symbol ?? "+";
}

export function stepMatheValue(value: string, change: -1 | 1) {
  const parsed = Number(value);
  const current = value.trim() !== "" && Number.isSafeInteger(parsed) ? parsed : 0;
  return String(current + change);
}

export function matheAdvancesAutomatically(inSyncSession: boolean) {
  return !inSyncSession;
}

export function Mathe({ account, config, pageId, adaptiveAssignmentId }: { account: Account; config: MatheExerciseConfig; pageId: string; adaptiveAssignmentId?: string }) {
  const labels = useRoleLabels();
  const startExerciseRun = useRecommendationRunStarter();
  const completion = useRunCompletion();
  const realtime = useRealtime();
  const facilitator = account.roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST");
  const inSyncSession = Boolean(realtime.session);
  const follower = inSyncSession && !facilitator;
  const canControl = !inSyncSession || facilitator;
  const canAnswer = !realtime.session || facilitator || follower;
  const [mode, setMode] = useState<MatheRunSettings["mode"]>(config.defaultMode);
  const [count, setCount] = useState(config.defaultCount);
  const [durationSeconds, setDurationSeconds] = useState(config.defaultDurationSeconds);
  const [range, setRange] = useState<MatheRunSettings["range"]>(config.defaultRange);
  const [selectedOperations, setSelectedOperations] = useState<MatheOperation[]>(config.defaultOperations);
  const canCreateAdaptive = !adaptiveAssignmentId
    && Boolean(account.modules?.adaptiveAssignments)
    && account.roles.some((role) => role === "ORGANIZATION" || role === "THERAPIST");
  const [adaptiveComposerOpen, setAdaptiveComposerOpen] = useState(false);
  const [adaptiveLearners, setAdaptiveLearners] = useState<AdaptiveLearner[]>([]);
  const [adaptiveLearnersLoaded, setAdaptiveLearnersLoaded] = useState(false);
  const [adaptiveLearnerId, setAdaptiveLearnerId] = useState(account.actingAs?.id ?? "");
  const [adaptiveTitle, setAdaptiveTitle] = useState("Rechentraining für zuhause");
  const [adaptiveMessage, setAdaptiveMessage] = useState("");
  const [recommendationPlan, setRecommendationPlan] = useState(emptyRecommendationPlan);
  const [adaptiveProfile, setAdaptiveProfile] = useState<"gentle" | "balanced" | "focused">("balanced");
  const [exercise, setExercise] = useState<State | null>(null);
  useLearningDisplayItem(exercise?.runId, exercise?.index ?? 0, Boolean(exercise && exercise.phase !== "complete"));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [, tick] = useState(0);
  const answerRef = useRef<HTMLInputElement>(null);
  const correctCount = useMemo(() => exercise?.answers.filter(({ correct }) => correct).length ?? 0, [exercise?.answers]);

  function publish(state: State | null) {
    realtime.publishExercise({ exerciseType: "mathe", pageId, state });
  }

  useEffect(() => {
    type SyncedState = Omit<State, "value" | "correctionCount" | "intermediateValues"> & Partial<Pick<State, "value" | "correctionCount" | "intermediateValues">>;
    const synced = realtime.state.exercise as { exerciseType?: string; pageId?: string; state?: SyncedState | null } | null;
    if (synced?.exerciseType === "mathe" && synced.pageId === pageId) {
      setExercise(synced.state ? { ...synced.state, value: synced.state.value ?? "", correctionCount: synced.state.correctionCount ?? 0, intermediateValues: synced.state.intermediateValues ?? [] } : null);
    }
  }, [realtime.state.exercise, pageId]);

  useEffect(() => {
    if (exercise?.phase === "running") answerRef.current?.focus();
  }, [exercise?.phase, exercise?.index]);

  useEffect(() => {
    if (!exercise?.endsAt || exercise.phase === "complete") return;
    const interval = window.setInterval(() => tick((current) => current + 1), 250);
    return () => window.clearInterval(interval);
  }, [exercise?.endsAt, exercise?.phase]);

  useEffect(() => {
    if (busy || !exercise?.endsAt || exercise.phase === "complete" || !canControl) return;
    const timeout = setExerciseTimeout(() => finish().catch((error) => setMessage(error.message)), Math.max(0, exercise.endsAt - exerciseNow(realtime.serverNow)) + 20);
    return () => clearExerciseTimeout(timeout);
  }, [exercise?.endsAt, exercise?.phase, exercise?.index, canControl, busy]);

  useEffect(() => {
    if (exercise?.phase !== "feedback" || !canControl || !matheAdvancesAutomatically(inSyncSession)) return;
    const timeout = setExerciseTimeout(() => advance().catch((error) => setMessage(error.message)), exercise.feedback?.correct ? 650 : 1_250);
    return () => clearExerciseTimeout(timeout);
  }, [exercise?.phase, exercise?.index, exercise?.feedback?.correct, canControl, inSyncSession, busy]);

  useEffect(() => {
    if (!canCreateAdaptive) return;
    let mounted = true;
    setAdaptiveLearnersLoaded(false);
    api<AdaptiveLearner[]>("/auth/acting-as/options")
      .then((learners) => {
        if (!mounted) return;
        setAdaptiveLearners(learners);
        setAdaptiveLearnerId((current) => {
          if (learners.some(({ id }) => id === current)) return current;
          const preferred = account.actingAs?.id ?? "";
          return learners.some(({ id }) => id === preferred) ? preferred : "";
        });
        setAdaptiveLearnersLoaded(true);
      })
      .catch(() => { if (mounted) { setAdaptiveLearners([]); setAdaptiveLearnersLoaded(true); } });
    return () => { mounted = false; };
  }, [account.actingAs?.id, canCreateAdaptive]);

  function settings(): MatheRunSettings {
    return { mode, count, durationSeconds, range, operations: selectedOperations };
  }

  async function start(runSettings = settings()) {
    if (busy || !canControl || (!adaptiveAssignmentId && !runSettings.operations.length)) return;
    setBusy(true);
    try {
      const response = adaptiveAssignmentId
        ? await startAdaptiveAssignmentRun<{ runId: string; exercise: MatheRun }>(adaptiveAssignmentId)
        : await startExerciseRun<{ runId: string; exercise: MatheRun }>({ contentPageId: pageId, settings: runSettings });
      const next: State = { runId: response.runId, run: response.exercise, index: 0, phase: "running", value: "", correctionCount: 0, intermediateValues: [], answers: [], feedback: null, endsAt: response.exercise.settings.mode === "time" ? exerciseNow(realtime.serverNow) + response.exercise.settings.durationSeconds * 1_000 : null, result: null };
      setExercise(next);
      setMessage("");
      publish(next);
    } finally { setBusy(false); }
  }

  async function createAdaptiveAssignment() {
    if (!canCreateAdaptive || !adaptiveLearnerId || !adaptiveTitle.trim() || !selectedOperations.length || busy) return;
    setBusy(true);
    try {
      await api("/learning/adaptive-assignments", {
        method: "POST",
        body: JSON.stringify({
          learnerId: adaptiveLearnerId,
          contentPageId: pageId,
          title: adaptiveTitle,
          message: adaptiveMessage,
          ...recommendationPlanInput(recommendationPlan),
          exerciseType: "mathe",
          config: { ...settings(), focusProfile: adaptiveProfile },
        }),
      });
      setAdaptiveComposerOpen(false);
      setAdaptiveMessage("");
      setRecommendationPlan(emptyRecommendationPlan);
      setMessage("Die adaptive Empfehlung wurde angelegt.");
    } finally { setBusy(false); }
  }

  async function abort() {
    if (exercise && exercise.phase !== "complete") await abortExerciseRun(exercise.runId);
  }

  async function back() {
    if (!exercise || busy || !canControl) return;
    setBusy(true);
    try {
      await abort();
      setMode(exercise.run.settings.mode);
      setCount(exercise.run.settings.count);
      setDurationSeconds(exercise.run.settings.durationSeconds);
      setRange(exercise.run.settings.range);
      setSelectedOperations(exercise.run.settings.operations);
      setExercise(null);
      publish(null);
    } finally { setBusy(false); }
  }

  async function remix() {
    if (!exercise || busy || !canControl) return;
    const runSettings = exercise.run.settings;
    setBusy(true);
    try { await abort(); } finally { setBusy(false); }
    await start(runSettings);
  }

  async function check() {
    if (!exercise || exercise.phase !== "running" || busy || !canAnswer) return;
    const item = exercise.run.items[exercise.index];
    const parsed = Number(exercise.value);
    if (!item || exercise.value.trim() === "" || !Number.isSafeInteger(parsed)) return;
    setBusy(true);
    try {
      await submitExerciseAnswer(exercise.runId, { type: "mathe", itemId: item.id, value: parsed, skipped: false, correctionCount: exercise.correctionCount, intermediateValues: exercise.intermediateValues });
      const feedback: MatheAnswer = { itemId: item.id, value: parsed, expected: item.result, correct: parsed === item.result };
      const next = { ...exercise, phase: "feedback" as const, answers: [...exercise.answers, feedback], feedback };
      setExercise(next);
      publish(next);
    } finally { setBusy(false); }
  }

  async function advance() {
    if (!exercise || busy || !canControl || exercise.phase === "complete") return;
    if ((exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.items.length) return finish();
    const next = { ...exercise, index: exercise.index + 1, phase: "running" as const, value: "", correctionCount: 0, intermediateValues: [], feedback: null };
    setExercise(next);
    publish(next);
  }

  function updateValue(value: string) {
    if (!exercise || exercise.phase !== "running" || busy || !canAnswer) return;
    const previous = exercise.value.trim();
    const previousNumber = Number(previous);
    const corrected = previous !== "" && Number.isSafeInteger(previousNumber) && value !== exercise.value && value.trim().length <= previous.length;
    const next = {
      ...exercise,
      value,
      correctionCount: exercise.correctionCount + (corrected ? 1 : 0),
      intermediateValues: corrected ? [...exercise.intermediateValues, previousNumber].slice(-100) : exercise.intermediateValues,
    };
    setExercise(next);
    publish(next);
  }

  async function skip() {
    if (!exercise || exercise.phase !== "running" || busy || !canControl) return;
    const item = exercise.run.items[exercise.index];
    if (!item) return;
    setBusy(true);
    try {
      await submitSkippedExerciseAnswer(exercise.runId, item.id, "mathe");
    } finally {
      setBusy(false);
    }
    await advance();
  }

  async function finish() {
    if (!exercise || exercise.phase === "complete" || busy || !canControl) return;
    await completion.complete(exercise.runId, (result) => {
      const next = { ...exercise, result, phase: "complete" as const };
      setExercise(next);
      publish(next);
      setMessage("");
    });
  }

  async function closeCompletedRun(repeat: boolean) {
    if (!exercise || exercise.phase !== "complete") return;
    if (repeat) await remix();
    else await back();
  }

  function toggleOperation(operation: MatheOperation) {
    setSelectedOperations((current) => current.includes(operation) ? current.filter((item) => item !== operation) : [...current, operation]);
  }

  if (completion.status.phase !== "idle") return <RunCompletion completion={completion} />;

  if (follower && !exercise) return <ExerciseWaitingCard className="mathe-exercise" />;

  if (!exercise) return <section className="mathe-exercise">
    <p className="quiz-instructions learning-text">{config.instructions}</p>
    {adaptiveAssignmentId && <div className="alert info"><strong>Adaptives Rechentraining</strong><p>Der nächste Durchgang wird aus den bisherigen Ergebnissen zusammengestellt. Unsichere Aufgaben werden gezielt wiederholt.</p></div>}
    {!adaptiveAssignmentId && <div className="mathe-settings">
      <fieldset><legend>Zahlenraum</legend><div className="mathe-option-grid">{ranges.map((item) => <label className="checkbox-label" key={item.id}><input type="radio" name={`${pageId}-mathe-range`} checked={range === item.id} onChange={() => setRange(item.id)} />{item.label}</label>)}</div></fieldset>
      <fieldset><legend>Rechenarten</legend><div className="mathe-option-grid operations">{operations.map((item) => <label className="checkbox-label" key={item.id}><input type="checkbox" checked={selectedOperations.includes(item.id)} onChange={() => toggleOperation(item.id)} /><strong>{item.symbol}</strong> {item.label}</label>)}</div></fieldset>
      <fieldset><legend>Durchlauf</legend><div className="mathe-run-settings"><div className="blitz-choice-row"><label className="checkbox-label"><input type="radio" name={`${pageId}-mathe-mode`} checked={mode === "count"} onChange={() => setMode("count")} />Feste Anzahl</label><label className="checkbox-label"><input type="radio" name={`${pageId}-mathe-mode`} checked={mode === "time"} onChange={() => setMode("time")} />Auf Zeit</label></div>{mode === "count" ? <label>Anzahl Aufgaben<select value={count} onChange={(event) => setCount(Number(event.target.value))}>{[5, 10, 15, 20].map((item) => <option key={item}>{item}</option>)}</select></label> : <ExerciseDurationSelect value={durationSeconds} onChange={setDurationSeconds} />}</div></fieldset>
    </div>}
    <button className="primary" disabled={busy || !canControl || (!adaptiveAssignmentId && !selectedOperations.length)} onClick={() => start().catch((error) => setMessage(error.message))}>{adaptiveAssignmentId ? "Nächsten Durchgang starten" : "Rechentraining starten"}</button>
    {!selectedOperations.length && <div className="alert info">Wähle mindestens eine Rechenart aus.</div>}
    {!adaptiveAssignmentId && canCreateAdaptive && <>
      <button className="secondary adaptive-create-trigger" disabled={busy || !selectedOperations.length || !adaptiveLearnersLoaded || adaptiveLearners.length === 0} onClick={() => setAdaptiveComposerOpen(true)}>Adaptive Empfehlung anlegen</button>
      {adaptiveLearnersLoaded && adaptiveLearners.length === 0 && <p className="muted">{labels.LEARNER.plural}: Keine freigegebenen Konten verfügbar.</p>}
      {adaptiveComposerOpen && <section className="adaptive-assignment-composer" role="dialog" aria-modal="true" aria-labelledby={`${pageId}-adaptive-title`}>
        <div className="adaptive-assignment-composer-heading"><div><p className="eyebrow">Adaptive Empfehlung</p><h2 id={`${pageId}-adaptive-title`}>Empfehlung für zuhause anlegen</h2></div><button type="button" className="secondary small" onClick={() => setAdaptiveComposerOpen(false)}>Schließen</button></div>
        <p className="muted">Die aktuell gewählten Einstellungen werden für jeden neuen Durchgang adaptiv neu zusammengestellt.</p>
        <div className="adaptive-settings-summary"><strong>Gewählte Einstellungen</strong><span>{ranges.find((item) => item.id === range)?.label ?? range} · {selectedOperations.map((operation) => operations.find((item) => item.id === operation)?.label ?? operation).join(", ")} · {mode === "count" ? `${count} Aufgaben` : `${durationSeconds / 60} Minuten`}</span></div>
        <label>{labels.LEARNER.singular}<select value={adaptiveLearnerId} onChange={(event) => setAdaptiveLearnerId(event.target.value)}><option value="">{labels.LEARNER.singular} auswählen</option>{adaptiveLearners.map((learner) => <option value={learner.id} key={learner.id}>{learner.displayName}</option>)}</select></label>
        <label>Titel<input value={adaptiveTitle} onChange={(event) => setAdaptiveTitle(event.target.value)} maxLength={160} /></label>
        <label>Anweisung<textarea value={adaptiveMessage} onChange={(event) => setAdaptiveMessage(event.target.value)} maxLength={1_000} placeholder={`Optionale Hinweise (${labels.LEARNER.singular})`} /></label>
        <RecommendationPlanFields value={recommendationPlan} onChange={setRecommendationPlan} />
        <label>Adaptive Gewichtung<select value={adaptiveProfile} onChange={(event) => setAdaptiveProfile(event.target.value as typeof adaptiveProfile)}><option value="gentle">Sanft</option><option value="balanced">Ausgewogen</option><option value="focused">Fokussiert</option></select></label>
        <div className="adaptive-assignment-composer-actions"><button type="button" className="secondary" onClick={() => setAdaptiveComposerOpen(false)}>Abbrechen</button><button type="button" className="primary" disabled={busy || !adaptiveLearnerId || !adaptiveTitle.trim()} onClick={() => createAdaptiveAssignment().catch((error) => setMessage(error.message))}>Adaptive Empfehlung speichern</button></div>
      </section>}
    </>}
    {message && <div className="alert info">{message}</div>}
  </section>;

  const item = exercise.run.items[exercise.index];
  const remaining = exercise.endsAt ? Math.max(0, Math.ceil((exercise.endsAt - exerciseNow(realtime.serverNow)) / 1_000)) : null;
  const last = (exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.items.length;
  return <section className="mathe-exercise">
    {canControl && exercise.phase !== "complete" && <ExerciseMenu><button className="secondary small" disabled={busy} onClick={() => back().catch((error) => setMessage(error.message))}>← Zurück zu den Einstellungen</button><button className="secondary small" disabled={busy} onClick={() => remix().catch((error) => setMessage(error.message))}>↻ Neu mischen</button></ExerciseMenu>}
    <div className="exercise-progress">{exercise.run.settings.mode === "time" ? `${exercise.answers.length} Aufgaben beantwortet · ${remaining ?? 0} s` : `Aufgabe ${Math.min(exercise.index + 1, exercise.run.items.length)} von ${exercise.run.items.length}`}</div>
    {exercise.phase === "complete" ? <><div className="reime-result"><strong>{exercise.result?.score ?? correctCount} von {exercise.result?.maxScore ?? exercise.answers.length} Aufgaben richtig</strong><p>Der Durchlauf ist abgeschlossen.</p></div>{canControl && <CompletedRunActions onFinish={() => closeCompletedRun(false)} onRepeat={() => closeCompletedRun(true)} onError={setMessage} />}{exercise.phase === "complete" && <CompletedRunHistory key={exercise.runId} runId={exercise.runId} />}</> : item && <>
      <div className="mathe-card">
        <div className="mathe-calculation" aria-label={`${item.left} ${operationSymbol(item.operation)} ${item.right}`}><span>{item.left.toLocaleString("de-DE")}</span><span className="operator">{operationSymbol(item.operation)}</span><span>{item.right.toLocaleString("de-DE")}</span><span>=</span><div className="mathe-answer-control"><input ref={answerRef} className={exercise.phase === "feedback" ? exercise.feedback?.correct ? "correct" : "wrong" : ""} type="text" inputMode="numeric" value={exercise.phase === "feedback" && exercise.feedback ? String(exercise.feedback.value) : exercise.value} disabled={exercise.phase !== "running" || busy || !canAnswer} onChange={(event) => updateValue(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") check().catch((error) => setMessage(error.message)); }} aria-label="Ergebnis" placeholder="?" /><button type="button" className="secondary mathe-answer-step" disabled={exercise.phase !== "running" || busy || !canAnswer} onClick={() => updateValue(stepMatheValue(exercise.value, 1))} aria-label="Ergebnis um eins erhöhen">▲</button><button type="button" className="secondary mathe-answer-step" disabled={exercise.phase !== "running" || busy || !canAnswer} onClick={() => updateValue(stepMatheValue(exercise.value, -1))} aria-label="Ergebnis um eins verringern">▼</button></div></div>
      </div>
      {exercise.phase === "feedback" && <div className={`answer-feedback ${exercise.feedback?.correct ? "correct" : "wrong"}`}>{exercise.feedback?.correct ? "Richtig gerechnet!" : <>Nicht ganz. Richtig ist <strong>{exercise.feedback?.expected.toLocaleString("de-DE") ?? ""}</strong>.</>}</div>}
      {exercise.phase === "running" && <div className="mathe-actions"><button className="primary" disabled={busy || !canAnswer || exercise.value.trim() === ""} onClick={() => check().catch((error) => setMessage(error.message))}>Überprüfen</button>{canControl && <button className="secondary" disabled={busy} onClick={() => skip().catch((error) => setMessage(error.message))}>Überspringen</button>}</div>}
      {exercise.phase === "feedback" && inSyncSession && canControl && <div className="mathe-actions"><button className="primary" disabled={busy} onClick={() => advance().catch((error) => setMessage(error.message))}>{last ? "Auswertung ansehen" : "Nächste Aufgabe"}</button></div>}
    </>}
    {message && <div className="alert info">{message}</div>}
  </section>;
}
